/**
 * x-filters directive.
 *
 * Manages collection/search filter state with AJAX section rendering. Listens
 * for filter change events, serialises the form into URL search parameters,
 * pushes the new URL to browser history (in non-preview mode), and morphs the
 * section HTML in place using the Shopify Section Rendering API.
 *
 * @options
 *   sectionId          - The Shopify section ID used to build the section render URL.
 *   resourceUrl        - The collection or search page pathname to fetch.
 *   preview            - When true, operates in preview mode: skips history updates
 *                        and uses `previewSectionId` instead (default: false).
 *   previewSectionId   - Section ID used in preview mode
 *                        (default: "render-collection-preview").
 *
 * @events listened
 *   filter-sort-change   - Fired by the sort control when sorting changes.
 *   filter-list-change   - Fired by filter checkboxes when a filter changes.
 *   filter-price-change  - Fired by the price range filter on change.
 *   filter-remove        - Fired by remove-filter buttons; uses `data-url`.
 *   filter-apply         - Fired by an apply button; passes `event.detail.form`.
 *   filters-clear-all    - Fired by the clear-all button; uses `data-url`.
 *   filters-updated      - Fired after a successful morph; updates the view-more button.
 *
 * @events dispatched
 *   filters-change        - Emitted before each section fetch with `{ query }`.
 *   filters-before-update - Emitted before each morph with the existing and new HTML.
 *   filters-updated       - Emitted after each successful morph with the fetch URL.
 *
 * @example
 *   <div x-filters="{ sectionId: 'main-collection', resourceUrl: '/collections/all' }">
 *     <form x-ref="form">…</form>
 *   </div>
 */
(() => {
  /**
   * Search query parameter key used to preserve the active search term across
   * filter updates.
   * @type {string}
   */
  const SEARCH_QUERY = "q";

  /**
   * Gets the search query parameter from the current URL.
   * @returns {string} The search query, or an empty string if absent.
   */
  const getSearchQuery = () => {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? "";
  };

  /**
   * Creates normalised URL parameters from form data. Removes empty price
   * range values, strips the `page` parameter, and re-applies the active
   * search query.
   * @param {FormData} formData - The form data to serialise.
   * @returns {URLSearchParams} The processed URL parameters.
   */
  const createURLParameters = (formData) => {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get("filter.v.price.gte") === "")
      newParameters.delete("filter.v.price.gte");
    if (newParameters.get("filter.v.price.lte") === "")
      newParameters.delete("filter.v.price.lte");

    newParameters.delete("page");

    const searchQuery = getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  };

  /**
   * Sorts URL search params alphabetically by key then value, so identical
   * filter combinations always produce the same URL string (cache key).
   * @param {URLSearchParams} searchParams - The params to sort in place.
   */
  const sortSearchParams = (searchParams) => {
    const entries = [...searchParams.entries()].sort((a, b) =>
      a[0] < b[0]
        ? -1
        : a[0] > b[0]
          ? 1
          : a[1] < b[1]
            ? -1
            : a[1] > b[1]
              ? 1
              : 0,
    );

    for (const key of new Set(searchParams.keys())) searchParams.delete(key);
    for (const [key, value] of entries) searchParams.append(key, value);
  };

  /**
   * Appends all entries from `searchParams` to the given URL object.
   * @param {URL} url - The URL to append parameters to.
   * @param {URLSearchParams} searchParams - The parameters to append.
   * @returns {URL} The modified URL.
   */
  const applySearchParams = (url, searchParams) => {
    for (const [key, value] of searchParams.entries()) {
      url.searchParams.append(key, value);
    }

    return url;
  };

  /**
   * Pushes the current filter parameters to the browser history as a new entry.
   * No-ops in preview mode.
   * @param {FormData} formData - The form data to serialise into the URL.
   */
  const updateURLHash = (formData) => {
    const url = new URL(window.location.href);
    const urlParameters = createURLParameters(formData);

    url.search = "";
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState(
      { urlParameters: urlParameters.toString() },
      "",
      url.toString(),
    );
  };

  const root = (
    Alpine,
    el,
    {
      sectionId,
      resourceUrl,
      preview = false,
      previewSectionId = "render-collection-preview",
    },
  ) => {
    Alpine.bind(el, () => ({
      /** Handles sort-order changes from the sorting control. */
      ["@filter-sort-change.stop"](event) {
        this.updateFilterAndSorting(
          event.target.closest("form") || event.target.form,
        );
      },

      /** Handles filter checkbox changes. */
      ["@filter-list-change.stop"](event) {
        this.updateFilters(event.target.closest("form") || event.target.form);
      },

      /** Handles price range filter changes. */
      ["@filter-price-change.stop"](event) {
        this.updatePriceFilterAndResults(
          event.target.closest("form") || event.target.form,
        );
      },

      /** Handles individual filter removal via `data-url`. */
      ["@filter-remove.stop"](event) {
        this.updateFiltersByURL(event.target.dataset.url);
      },

      /** Handles the apply-filters action with an explicit form reference. */
      ["@filter-apply.stop"](event) {
        this.updateFilters(event.detail?.form);
      },

      /** Handles clearing all filters via `data-url`. */
      ["@filters-clear-all.stop"](event) {
        this.updateFiltersByURL(event.target.dataset.url);
      },

      /**
       * Updates the view-more button visibility and href after a section morph.
       * Shows the button when the number of results exceeds the per-page limit.
       */
      ["@filters-updated"]() {
        const { viewMoreButton } = this.$refs;
        const { url } = this.$event.detail;

        if (viewMoreButton && url) {
          const { perPage, resultsSize } = this.$root.dataset;

          const modUrl = new URL(url.toString());
          modUrl.searchParams.delete("section_id");

          viewMoreButton.style.contentVisibility =
            Number(perPage) < Number(resultsSize) ? "visible" : "hidden";
          viewMoreButton.querySelector("a.button").href = modUrl.toString();
        }
      },

      /** Triggers a section update when the element enters the viewport in preview mode. */
      ["x-intersect:enter.margin.200px"]() {
        if (preview && sectionId != "collection-preview") {
          this.updateSection();
        }
      },

      ["x-data"]() {
        return {
          loading: false,

          /**
           * Prefetches the section HTML for a hypothetical filter state based
           * on what would happen if the hovered filter item were toggled.
           * @param {MouseEvent} event - The pointer event from a filter item.
           */
          handlePrefetchPage(event) {
            if (!(event.target instanceof HTMLElement)) return;

            const form = this.$root.closest("form");
            if (!form) return;

            const formData = new FormData(form);

            const inputElement = event.target.querySelector("input");
            if (!(inputElement instanceof HTMLInputElement)) return;

            if (!inputElement.checked)
              formData.append(inputElement.name, inputElement.value);

            const urlParameters = createURLParameters(formData);

            const url = new URL(window.location.origin);

            for (const [key, value] of urlParameters)
              url.searchParams.append(key, value);

            if (inputElement.checked)
              url.searchParams.delete(inputElement.name, inputElement.value);

            const requestSectionId = preview ? previewSectionId : sectionId;

            url.pathname = resourceUrl;
            url.searchParams.set("section_id", requestSectionId);
            url.searchParams.delete("oseid"); // Special param in theme editor.

            sortSearchParams(url.searchParams);

            this.$app.$prefetchManager.fetchLatest(
              url.toString(),
              "x-filters-prefetch",
            );
          },

          /**
           * Aborts any in-flight prefetch request for filter pages.
           */
          cancelPrefetchPage() {
            const { $prefetchManager } = this.$app;

            $prefetchManager.abort("x-filters-prefetch");
          },

          /**
           * Delegates to `updateFilters` for price range changes.
           * @param {HTMLFormElement} form - The filter form.
           */
          updatePriceFilterAndResults(form) {
            this.updateFilters(form);
          },

          /**
           * Delegates to `updateFilters` for sort order changes.
           * @param {HTMLFormElement} form - The filter form.
           */
          updateFilterAndSorting(form) {
            this.updateFilters(form);
          },

          /**
           * Serialises the filter form, updates the URL, and fetches the updated
           * section HTML.
           * @param {HTMLFormElement|null} form - The filter form element. Falls back
           *   to `$refs.form` when null.
           */
          updateFilters(form) {
            form = form || this.$refs.form;
            const formData = new FormData(form);
            const parameters = createURLParameters(formData);

            if (!preview) {
              updateURLHash(formData);
            }

            this.$dispatch("filters-change", {
              query: parameters,
            });

            let url;

            if (preview) {
              url = applySearchParams(
                new URL(window.location.href),
                parameters,
              );
            }

            this.updateSection({ url });
          },

          /**
           * Navigates to the given URL, updates the browser history, and fetches
           * the corresponding section HTML.
           * @param {string} url - The URL to navigate to (from `data-url`).
           */
          updateFiltersByURL(url) {
            if (!preview) {
              history.pushState("", "", url);
            }

            const formData = new FormData(this.$refs.form);
            const parameters = createURLParameters(formData);

            sortSearchParams(parameters);

            this.$dispatch("filters-change", {
              query: parameters,
            });

            let newUrl;

            if (preview) {
              newUrl = new URL(window.location.href);
              const filtersUrl = new URL(url, window.location.origin);

              newUrl = applySearchParams(newUrl, filtersUrl.searchParams);
            }

            this.updateSection({
              url: newUrl,
            });
          },

          /**
           * Fetches updated section HTML from the Shopify Section Rendering API
           * and morphs the current section in place. Sets `loading` to true
           * during the request and false once complete.
           * @param {{ url?: URL }} [options] - Optional URL override; defaults to
           *   the current page URL.
           * @returns {Promise<void>}
           */
          async updateSection({ url = new URL(window.location.href) } = {}) {
            const requestSectionId = preview ? previewSectionId : sectionId;

            url.pathname = resourceUrl;
            url.searchParams.set("section_id", requestSectionId);
            url.searchParams.delete("oseid"); // Special param in theme editor.

            sortSearchParams(url.searchParams);

            this.loading = true;

            await this.$app.$prefetchManager
              .fetchLatest(url.toString(), "x-filters")
              .then((result) => result.text())
              .then((htmlText) => {
                const html = new DOMParser().parseFromString(
                  htmlText,
                  "text/html",
                );

                const sectionHtml = html.querySelector("[x-filters]");

                if (!sectionHtml) {
                  throw new Error("No section found");
                }

                const existingHtml = this.$root;

                this.$dispatch("filters-before-update", {
                  url,
                  sectionId,
                  preview,
                  html: {
                    existing: existingHtml,
                    replaceWith: sectionHtml,
                  },
                });

                this.$morph(existingHtml, sectionHtml);

                this.$dispatch("filters-updated", {
                  url,
                  sectionId,
                  preview,
                  html,
                });
              })
              .catch((error) => {
                if (error.name === "AbortError") {
                  console.warn("Fetch aborted by user.");
                } else {
                  console.error(error);
                }
              })
              .finally(() => {
                this.loading = false;
              });
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("filters", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};

      root(Alpine, el, options);
    }).before("bind");
  });
})();
